/*
 * Copyright (c) Nexign, JSC, 2020-2022.
 */
/**
 * Component for Auto Reports tab
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.3.0
 */
import { connect, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { DropFiltersButton, InfoBlock, LinkBlock } from '@components/common';
import { dropTableFilterAndSort } from '@components/Table/actions/tableActions';
import {
    makeSelectorTableCount, makeSelectorTableIsFilterSelected,
} from '@components/Table/selectors/tableSelectors';
import { TableNameType } from '@components/Table/types/tableTypes';
import { EColorStatus } from '@modules/app/appConstants';
import DensityButton from '@modules/density/components/DensityButton';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectPageReportsConstructorTranslation, makeSelectPageSettingsAutoReportsTranslation } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IPageSettingsAutoReportsTranslation } from '@modules/internationalization/types/pages/pageSettings';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { EReportsConstructorSource } from '@pages/PageReportsConstructor/constants';
import { IRootReducer } from '@reducers/rootReducer';
import { RoutePath } from '@routes/constants/routesConstants';

import {
    PageSettingsCounterStyled,
    PageSettingsHeaderContainerStyled,
    PageSettingsHeaderStyled,
    PageTitleElementsContainerStyled,
} from '../../styled/PageSettingsStyled';
import { SettingsAutoReportsContainerStyled, SettingsAutoReportsLinkContainerStyled, SettingsAutoReportsLinkTitleStyled } from '../styled/SettingsAutoReportsStyled';
import SettingsAutoReportsTable from './SettingsAutoReportsTable/SettingsAutoReportsTable';

export type TSettingsAutoReports = IConnectedProps & IDispatchProps;

export const SettingsAutoReports = (props: TSettingsAutoReports) => {
    const isReportsConstructorAvailable = useSelector(
        makeSelectGuardComponentEnabled(AccessComponentKeyEnum.reportsConstructor),
    );
    const tReportsContructor = useSelector(makeSelectPageReportsConstructorTranslation());

    return (
        <SettingsAutoReportsContainerStyled data-module="settingsAutoReports">
            {isReportsConstructorAvailable ? (
                <>
                    <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageReportsConstructor]} />
                    <InfoBlock colorStatus={EColorStatus.INFO}>
                        <SettingsAutoReportsLinkContainerStyled>
                            <SettingsAutoReportsLinkTitleStyled>
                                {tReportsContructor.pageSettingsAutoReportsLink}
                            </SettingsAutoReportsLinkTitleStyled>
                            <LinkBlock
                                linkState={{ source: EReportsConstructorSource.AUTO_REPORTS }}
                                url={RoutePath.reportsConstructorFromAutoReports}
                            >
                                {`“${tReportsContructor.header.title}“`}
                            </LinkBlock>
                        </SettingsAutoReportsLinkContainerStyled>
                    </InfoBlock>
                </>
            ) : (
                <>
                    <PageSettingsHeaderContainerStyled>
                        <PageSettingsHeaderStyled>
                            {props.autoReportsTranslation.title.header}
                            <PageSettingsCounterStyled data-name="autoReportsCount">
                                {props.tableCount}
                            </PageSettingsCounterStyled>
                        </PageSettingsHeaderStyled>

                        <PageTitleElementsContainerStyled>
                            {
                                props.isFilterSelected &&
                                <DropFiltersButton
                                    label={props.autoReportsTranslation.title.dropFilters}
                                    onClick={props.dropFilters}
                                />
                            }
                            <DensityButton page={ECommonTranslationPages.settings} />
                        </PageTitleElementsContainerStyled>
                    </PageSettingsHeaderContainerStyled>

                    <SettingsAutoReportsTable
                        tableCount={props.tableCount}
                    />
                </>
            )}
        </SettingsAutoReportsContainerStyled>
    )
};

interface IConnectedProps {
    autoReportsTranslation: IPageSettingsAutoReportsTranslation;
    isFilterSelected: boolean;
    tableCount: number;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    autoReportsTranslation: makeSelectPageSettingsAutoReportsTranslation(),
    isFilterSelected: makeSelectorTableIsFilterSelected(TableNameType.settingsAutoReports),
    tableCount: makeSelectorTableCount(TableNameType.settingsAutoReports),
});

interface IDispatchProps {
    dropFilters: () => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    dropFilters: () => dispatch(dropTableFilterAndSort({ tableName: TableNameType.settingsAutoReports })),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsAutoReports);
